import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import { ICharacterSkillLevel, IDailyTask, rage, useObservable } from '~/rage';
import { CharacterSkill } from '~/shared';
import {
	CaseItem,
	CaseType as CaseTypes,
	ICaseItem,
	calcOldPrice,
} from '../CaseItem';
import { MainBanner } from '../MainBanner';
import { News } from '../News';
import { Promocode } from '../Promocode';
import { StatsSkillItem } from '../StatsSkillItem';
import {
	ISkillData,
	jobSkills,
	skillNames,
	sortSkillsForMain,
} from '../StatsSkillItem/StatsSkillItem.model';
import { Task } from '../Task';
import { TaskTimer } from '../TaskTimer';
import { StyledContainer, StyledLeftContainer } from './Main.styled';

const getRandomTask = (
	taskList: IDailyTask[],
	excludedTasks: IDailyTask[],
): IDailyTask | null => {
	const availableTasks = taskList.filter(
		(task) =>
			!excludedTasks.some((excludedTask) => excludedTask.id === task.id) &&
			task.currentCount < task.targetCount,
	);
	if (availableTasks.length === 0) {
		return null;
	}
	const randomIndex = Math.floor(Math.random() * availableTasks.length);
	return availableTasks[randomIndex];
};

const Main = () => {
	const cases = useObservable(rage.cases.getLootboxes());
	const bundles = useObservable(rage.cases.getBundles());
	const allTasks = useObservable(rage.dailyTasks.getTasks());
	const taskList = useMemo(
		() => allTasks.filter((task) => !task.group || task.group === 'main'),
		[allTasks],
	);
	const unfinishedTasks = useMemo(
		() => taskList.filter((task) => task.currentCount < task.targetCount),
		[taskList],
	);
	const slotCount = 2;
	const unfinishedSlotCount = Math.min(slotCount, unfinishedTasks.length);
	// Если есть хотя бы одна невыполненная задача — заполняем оставшийся слот таймером.
	// Если все задачи выполнены — показываем таймер столько раз, сколько задач всего (как в QuestTab),
	// чтобы не переезжала сетка.
	const timerSlotCount =
		taskList.length > 0
			? unfinishedSlotCount > 0
				? slotCount - unfinishedSlotCount
				: Math.min(slotCount, taskList.length)
			: 0;
	const taskExpire = useObservable(rage.dailyTasks.getTasksExpireAt());
	const [randomTasks, setRandomTasks] = useState<IDailyTask[]>([]);

	const [skillsData, setSkillsData] = useState<
		Record<CharacterSkill, ICharacterSkillLevel>
	>(() => {
		const initial: Record<CharacterSkill, ICharacterSkillLevel> = {} as any;
		jobSkills.forEach((skill) => {
			initial[skill] = rage.skills.getSkillLevel(skill).getValue();
		});
		return initial;
	});

	const filteredCases = useMemo(
		() =>
			cases
				.filter((item) => item.showOnHome)
				.map(
					(item) =>
						({
							id: item.id,
							type: CaseTypes.Default,
							name: item.name,
							discount: item.discount,
							price: item.price ?? 0,
							wallet: item.walletType,
							oldPrice: calcOldPrice(item.price ?? 0, item.discount ?? 0),
							tags: item.tags,
							image: item.id,
							dateDiscountEnd: item?.discountTimestampEnd,
							dateEnd: item?.enabledTimestampEnd,
						}) as ICaseItem,
				),
		[cases],
	);

	const filteredKits = useMemo(
		() =>
			bundles
				.filter((item) => item.showOnHome)
				.map(
					(item) =>
						({
							id: item.id,
							type: CaseTypes.Kit,
							name: item.name,
							discount: item.discount,
							price: item.price ?? 0,
							wallet: item.walletType,
							oldPrice: calcOldPrice(item.price ?? 0, item.discount ?? 0),
							tags: item.tags,
							image: item.id,
							dateDiscountEnd: item?.discountTimestampEnd,
							dateEnd: item?.enabledTimestampEnd,
						}) as ICaseItem,
				),
		[bundles],
	);

	const firstRowColumnsUsed = useMemo(() => {
		let used = 0;
		if (filteredKits?.[0]) used += 2;
		if (filteredCases?.[0]) used += 1;
		if (filteredCases?.[1]) used += 1;
		return used;
	}, [filteredKits, filteredCases]);

	const missingCellsFirstRow = useMemo(
		() => Math.max(0, 4 - firstRowColumnsUsed),
		[firstRowColumnsUsed],
	);

	const missingCellsSecondRow = useMemo(() => {
		const plannedTaskCards = unfinishedSlotCount;
		const plannedSpan2Items = plannedTaskCards + timerSlotCount;
		return Math.max(0, slotCount - plannedSpan2Items);
	}, [unfinishedSlotCount, timerSlotCount]);

	const replaceTask = useCallback(
		(index: number) => {
			setRandomTasks((prevTasks) => {
				const newTask = getRandomTask(taskList, prevTasks);
				if (!newTask) {
					return prevTasks;
				}

				const newTasks = [...prevTasks];
				newTasks[index] = newTask;
				return newTasks;
			});
		},
		[taskList],
	);

	useEffect(() => {
		const availableTasks = taskList.filter(
			(task) => task.currentCount < task.targetCount,
		);

		setRandomTasks((prevTasks) => {
			const desiredCount = Math.min(2, availableTasks.length);
			if (desiredCount === 0) {
				return [];
			}

			const nextTasks = prevTasks
				.map((prevTask) =>
					availableTasks.find((task) => task.id === prevTask.id),
				)
				.filter((task): task is IDailyTask => Boolean(task))
				.slice(0, desiredCount);

			while (nextTasks.length < desiredCount) {
				const additionalTask = getRandomTask(availableTasks, nextTasks);
				if (!additionalTask) {
					break;
				}

				nextTasks.push(additionalTask);
			}

			return nextTasks;
		});
	}, [taskList]);

	useEffect(() => {
		const subscriptions = jobSkills.map((skill) => {
			return rage.skills.getSkillLevel(skill).subscribe((data) => {
				setSkillsData((prev) => ({
					...prev,
					[skill]: data,
				}));
			});
		});

		return () => {
			subscriptions.forEach((sub) => sub.unsubscribe());
		};
	}, []);

	const mainSkills = useMemo(() => {
		const skills: ISkillData[] = jobSkills.map((skill) => ({
			skill,
			name: skillNames[skill],
			level: skillsData[skill]?.level ?? 1,
			maxLevel: skillsData[skill]?.maxLevel ?? 1,
			exp: skillsData[skill]?.exp ?? 0,
			maxExp: skillsData[skill]?.maxExp ?? 0,
		}));
		return sortSkillsForMain(skills).slice(0, 4);
	}, [skillsData]);

	return (
		<StyledContainer>
			<StyledLeftContainer>
				<MainBanner />
				<Promocode />
				{filteredKits.length > 0 && (
					<Fragment>
						{filteredKits?.[0] && <CaseItem span={2} {...filteredKits?.[0]} />}
					</Fragment>
				)}
				{filteredCases.length > 0 && (
					<Fragment>
						{filteredCases?.[0] && <CaseItem {...filteredCases?.[0]} />}
						{filteredCases?.[1] && <CaseItem {...filteredCases?.[1]} />}
					</Fragment>
				)}
				{missingCellsFirstRow > 0 && (
					<Fragment>
						{new Array(missingCellsFirstRow).fill(null).map((_, index) => (
							<div key={`filler-${index}`} />
						))}
					</Fragment>
				)}
				{/* {randomTasks.length < 2 && <UpdateTask time={seconds} />} */}
				{unfinishedSlotCount > 0 &&
					randomTasks.map((item, index) => (
						<SwitchTransition key={index}>
							<CSSTransition
								timeout={300}
								classNames={'task'}
								key={item.id}
								addEndListener={(node, done) =>
									node.addEventListener('transitionend', done, false)
								}
							>
								<Task
									id={item.id}
									name={item.name}
									text={item.description}
									exp={item.rewardExp || 0}
									amount={item.rewardMoney || 0}
									progress={(item.currentCount / item.targetCount) * 100}
									type="daily"
									onRefresh={() => replaceTask(index)}
									span={2}
								/>
							</CSSTransition>
						</SwitchTransition>
					))}
				{timerSlotCount > 0 &&
					new Array(timerSlotCount)
						.fill(null)
						.map((_, index) => (
							<TaskTimer
								key={`main-timer-${index}`}
								taskExpire={taskExpire}
								span={2}
							/>
						))}
				{missingCellsSecondRow > 0 && (
					<Fragment>
						{new Array(missingCellsSecondRow).fill(null).map((_, index) => (
							<div key={index} style={{ gridColumn: 'span 2' }} />
						))}
					</Fragment>
				)}
				{mainSkills.map((skillData) => (
					<StatsSkillItem
						name={skillData.name}
						skill={skillData.skill}
						level={skillData.level}
						current={skillData.exp}
						target={skillData.maxExp}
						isMaxLevel={skillData.level === skillData.maxLevel}
						key={skillData.skill}
					/>
				))}
			</StyledLeftContainer>
			<News />
		</StyledContainer>
	);
};

export { Main };
